import { CheckCircle2, AlertTriangle, ExternalLink, FileText, Users, Calendar, Euro, Sparkles } from "lucide-react";

/**
 * Dossier Flash B2B — 4 blocs stricts (Loi 71-1130, Etalab 2.0)
 * Encart de tête : Reformulation factuelle qualifiée (neutre, sans opinion)
 * Bloc 1 : Faits & Chronologie · Bloc 2 : Ready Score · Bloc 3 : Checklist · Bloc 4 : Traçabilité Open Data
 */
export function DossierFlash({ data, animated = false }) {
  if (!data) return null;
  const reformulation = (data.reformulation_situation || "").trim();
  const b1 = data.block1_faits || {};
  const b2 = data.block2_ready_score || {};
  const b3 = data.block3_checklist || {};
  const b4 = data.block4_tracabilite || [];
  const score = Math.min(100, Math.max(0, Number(b2.pourcentage || 0)));

  return (
    <div className="space-y-4" data-testid="dossier-flash">
      {/* ENCART — Reformulation factuelle qualifiée (en tête) */}
      {reformulation && (
        <div className={`bg-slate-50 border border-slate-200 rounded-xl p-4 ${animated ? "fade-up" : ""}`} data-testid="dossier-reformulation">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <div className="j-label flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Synthèse factuelle de la situation déclarée</div>
            <span className="text-[0.6rem] uppercase tracking-widest font-black px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 border border-slate-300">Qualification neutre</span>
          </div>
          <p className="text-slate-800 text-sm leading-relaxed" data-testid="dossier-reformulation-text">{reformulation}</p>
        </div>
      )}

      {/* BLOC 1 — Faits & Chronologie */}
      <div className={`j-card ${animated ? "fade-up" : ""}`} data-testid="dossier-block-1">
        <div className="j-label mb-2 flex items-center gap-1.5"><FileText className="w-3 h-3" /> Bloc 1 · Faits & Chronologie</div>
        {b1.chronologie?.length > 0 && (
          <ol className="space-y-2 mt-3">
            {b1.chronologie.map((e, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <span className="font-mono text-xs text-slate-500 flex-shrink-0 mt-0.5"><Calendar className="w-3 h-3 inline mr-1" />{e.date || "?"}</span>
                <span className="text-slate-800">{e.fait}</span>
              </li>
            ))}
          </ol>
        )}
        <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-slate-100">
          {b1.montants?.length > 0 && (
            <div>
              <div className="text-[0.65rem] uppercase tracking-widest text-slate-500 font-bold mb-1"><Euro className="w-3 h-3 inline mr-1" /> Montants</div>
              <ul className="text-sm text-slate-800 space-y-0.5">{b1.montants.map((m, i) => <li key={i}>· {m}</li>)}</ul>
            </div>
          )}
          {b1.parties?.length > 0 && (
            <div>
              <div className="text-[0.65rem] uppercase tracking-widest text-slate-500 font-bold mb-1"><Users className="w-3 h-3 inline mr-1" /> Parties</div>
              <ul className="text-sm text-slate-800 space-y-0.5">{b1.parties.map((p, i) => <li key={i}>· {p}</li>)}</ul>
            </div>
          )}
        </div>
      </div>

      {/* BLOC 2 — Ready Score */}
      <div className={`j-card ${animated ? "fade-up d-100" : ""}`} data-testid="dossier-block-2">
        <div className="j-label mb-2">Bloc 2 · Dossier Ready Score</div>
        <div className="flex items-center gap-4 mt-3">
          <div className="relative w-24 h-24 flex-shrink-0">
            <svg viewBox="0 0 100 100" className="transform -rotate-90">
              <circle cx="50" cy="50" r="42" stroke="#E2E8F0" strokeWidth="10" fill="none" />
              <circle cx="50" cy="50" r="42" stroke={score >= 75 ? "#10B981" : score >= 50 ? "#F59E0B" : "#DC2626"} strokeWidth="10" fill="none"
                strokeDasharray={`${(score / 100) * 264} 264`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-2xl font-black text-[#1E293B]" data-testid="dossier-score">{score}%</span>
            </div>
          </div>
          <div className="flex-1">
            <div className="font-heading font-black text-lg text-[#1E293B]">{b2.libelle || `${score}% — Complétude`}</div>
            <p className="text-xs text-slate-600 mt-1">Ratio pièces présentes / pièces requises. Loi 71-1130 : information seule, aucune analyse de fond.</p>
          </div>
        </div>
      </div>

      {/* BLOC 3 — Checklist de complétude */}
      <div className={`j-card ${animated ? "fade-up d-200" : ""}`} data-testid="dossier-block-3">
        <div className="j-label mb-2">Bloc 3 · Checklist de complétude</div>
        <div className="grid md:grid-cols-2 gap-4 mt-3">
          <div>
            <div className="text-[0.7rem] uppercase tracking-widest text-emerald-700 font-bold mb-2">Pièces présentes</div>
            <ul className="space-y-1.5">
              {(b3.pieces_presentes || []).map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm" data-testid={`piece-present-${i}`}>
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-800">{p}</span>
                </li>
              ))}
              {(!b3.pieces_presentes || b3.pieces_presentes.length === 0) && <li className="text-xs italic text-slate-500">Aucune pièce détectée.</li>}
            </ul>
          </div>
          <div>
            <div className="text-[0.7rem] uppercase tracking-widest text-amber-700 font-bold mb-2">Pièces manquantes</div>
            <ul className="space-y-1.5">
              {(b3.pieces_manquantes || []).map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm" data-testid={`piece-missing-${i}`}>
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-800">{p}</span>
                </li>
              ))}
              {(!b3.pieces_manquantes || b3.pieces_manquantes.length === 0) && <li className="text-xs italic text-emerald-700">Dossier complet.</li>}
            </ul>
          </div>
        </div>
      </div>

      {/* BLOC 4 — Traçabilité Open Data */}
      <div className={`j-card ${animated ? "fade-up d-300" : ""}`} data-testid="dossier-block-4">
        <div className="j-label mb-2">Bloc 4 · Traçabilité Open Data</div>
        <p className="text-[0.7rem] text-slate-500 mb-3">Sources publiques uniquement (Licence Etalab 2.0). Cliquez pour vérifier.</p>
        <ul className="space-y-2">
          {b4.map((ref, i) => (
            <li key={i} className="flex items-center gap-3 p-2 rounded-lg border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/30 transition-colors">
              <span className="text-[0.6rem] uppercase tracking-widest font-black px-2 py-0.5 rounded-full bg-slate-800 text-emerald-300 flex-shrink-0">{ref.source}</span>
              <a href={ref.url} target="_blank" rel="noopener noreferrer" className="text-sm font-mono text-slate-800 hover:text-emerald-700 underline decoration-dotted flex-1 truncate" data-testid={`open-data-link-${i}`}>
                {ref.texte}
              </a>
              <ExternalLink className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            </li>
          ))}
          {b4.length === 0 && <li className="text-xs italic text-slate-500">Aucune référence Open Data rattachée.</li>}
        </ul>
      </div>
    </div>
  );
}
